import { ensureAuthToken } from 'owa-groups-sharepoint-commands/lib/authentication';
import {
    getSharePointResourceTokenPrefetchState,
    updateSharePointResourceTokenPrefetchStateAction,
    updateSharePointResourceTokenPrefetchErrorStateAction,
    SharePointResourceTokenPrefetchStatus,
    SharePointResourceTokenPrefetchError,
} from 'owa-group-files-hub-store';
import { isAuthTokenError } from 'owa-groups-sharepoint-commands';

// This purpose of this function, apart from fetching token is to make sure we do not make duplicate call to fetch the sharepoint token and await if a call is already made to get the token.
// So the caller of this function can use the promise returned by this function to make sure its not making duplicate calls to get SP token.
// Apart from SharePointResourceTokenPrefetchStatus.Requested case, in all the cases we resolve the promise. In this case we reject as we don't want the caller to make additional call as we have already made the call.
// The reason is that we want the caller to go ahead and make the call to ensureAuthToken.
// in case of token available, ensureAuthToken will return from cache
export function startSharePointResourceTokenFetch(resourceUrl: string): Promise<void> {
    if (!resourceUrl) {
        // If resourceUrl is null or undefined or empty string, reject with an error
        return Promise.reject<void>(new Error('resourceUrl is null or undefined or empty string'));
    }

    const currentResourceTokenStatus = getSharePointResourceTokenPrefetchState(resourceUrl);

    switch (currentResourceTokenStatus.status) {
        case SharePointResourceTokenPrefetchStatus.Available:
            // if token is already available, resolve promise to indicate that token is available to go ahead
            return Promise.resolve(); // to indicate token is available, go ahead and get it from the cache
        case SharePointResourceTokenPrefetchStatus.Requested:
            // to indicate not started fetch as already started
            return Promise.reject<void>(new Error('Already started the fetch'));
        case SharePointResourceTokenPrefetchStatus.NotAvailable:
        case SharePointResourceTokenPrefetchStatus.Error:
            // if it's an ADAL exception while getting token, we don't want to proceed further or make more calls to get the token as it would not succeed in the same session
            if (
                currentResourceTokenStatus.status == SharePointResourceTokenPrefetchStatus.Error &&
                currentResourceTokenStatus.error == SharePointResourceTokenPrefetchError.AdalError
            ) {
                return Promise.reject<void>(
                    new Error('Permanent error while fetching token. Retry wont work')
                );
            }

            // only start fetching the token if it's not started or the previous state is error but not ADAL error (which is permanent and can't be resolved in the same session)
            updateSharePointResourceTokenPrefetchStateAction(
                resourceUrl,
                SharePointResourceTokenPrefetchStatus.Requested
            );
            return ensureAuthToken(resourceUrl, 'GroupFilesHubTokenPrefetch')
                .then(response => {
                    // to indicate token is now available, go ahead and get it from the cache
                    updateSharePointResourceTokenPrefetchStateAction(
                        resourceUrl,
                        SharePointResourceTokenPrefetchStatus.Available
                    );
                })
                .catch(error => {
                    if (isAuthTokenError(error) && error.isAdalException == true) {
                        updateSharePointResourceTokenPrefetchErrorStateAction(
                            resourceUrl,
                            SharePointResourceTokenPrefetchError.AdalError
                        );

                        return Promise.reject<void>(
                            new Error('Permanent error while fetching token. Retry wont work')
                        );
                    } else {
                        // to indicate that unable to get the token, go ahead and call ensureAuthToken to get it from server again
                        updateSharePointResourceTokenPrefetchErrorStateAction(resourceUrl);

                        return Promise.resolve(); // we resolve the promise here as this could be a transient error and retrying to get the token might work.
                    }
                });
    }
}
